import { isEmpty, isNumber } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Avatar } from '@mui/material';

import useIsMobile from 'hooks/use-is-mobile';

import useStyles from './styles';

import useFormatDate from '../../hooks/use-format-date';
import { FormattedDataItem } from '../../types';
import Label from '../Label';
import Score from '../Score';
import Tag from '../Tag';

interface Props {
    data: FormattedDataItem;
    className?: string;
}

const ListItem: React.FC<Props> = ({ data, className }) => {
    const { classes, cx } = useStyles();
    const isMobile = useIsMobile();
    const formatDate = useFormatDate();
    const [isExpanded, setIsExpanded] = useState(data.exact);
    const hasLabels = useMemo(() => {
        const {
            exact,
            isWild,
            references,
            exploits,
            affectedSoftware,
            affectedPackages,
            affectedConfiguration,
            webApplicable
        } = data;
        return (
            isNumber(exploits) ||
            [exact, isWild, references, affectedSoftware, affectedPackages, affectedConfiguration, webApplicable].some(
                Boolean
            )
        );
    }, [data]);

    return (
        <Link to={data.href} className={cx(classes.container, { [classes.exact]: data.exact }, className)}>
            <div className={classes.content}>
                <div className={classes.header}>
                    <span className={classes.type}>
                        <Avatar className={classes.avatar} src={data.image} alt={data.type} />
                        {data.type}
                    </span>
                    <span className={classes.dot}>{'•'}</span>
                    <span className={classes.published}>
                        {isMobile ? formatDate(data.published) : `added ${formatDate(data.published)}`}
                    </span>
                    <span className={classes.dot}>{'•'}</span>
                    <span className={classes.views}>
                        <VisibilityOutlinedIcon />
                        {isMobile ? data.views : `${data.views} views`}
                    </span>
                </div>
                <div className={classes.text}>
                    <p className={classes.title}>{data.title}</p>
                    <p className={classes.description}>{data.description}</p>
                </div>
                <div className={classes.scores}>
                    {data.cvssScore ? <Score value={data.cvssScore} label="CVSS" /> : null}
                    {data.aiScore ? <Score value={data.aiScore} label="AI score" /> : null}
                    {data.epssScore ? <Score value={data.epssScore} label="EPSS" scale={0.1} /> : null}
                    {isEmpty(data.tags) || isExpanded ? null : (
                        <MoreHorizOutlinedIcon
                            className={classes.more}
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setIsExpanded(true);
                            }}
                        />
                    )}
                </div>
                {data.tags.length && isExpanded ? (
                    <div className={classes.tags}>
                        {data.tags.map((label, index) => (
                            <Tag key={index} label={label} />
                        ))}
                    </div>
                ) : null}
            </div>
            {hasLabels ? (
                <div className={classes.labels}>
                    {data.isWild ? (
                        <Label
                            compact={isMobile}
                            icon={<ReportOutlinedIcon />}
                            variant="danger-contained"
                            text="In wild"
                        />
                    ) : null}
                    {isNumber(data.exploits) ? (
                        <Label
                            compact={isMobile}
                            text="Exploits"
                            variant={data.exploits > 0 ? 'danger-outlined' : 'default'}
                            count={data.exploits}
                        />
                    ) : null}
                    {data.webApplicable ? (
                        <Label compact={isMobile} icon={<LanguageOutlinedIcon />} text="Web" />
                    ) : null}
                    {data.exact ? <Label compact={isMobile} icon={<ThumbUpOutlinedIcon />} text="Match" /> : null}
                    {data.references ? <Label compact={isMobile} text="References" count={data.references} /> : null}
                    {data.affectedSoftware ? (
                        <Label compact={isMobile} text="Affected Software" count={data.affectedSoftware} />
                    ) : null}
                    {data.affectedPackages ? (
                        <Label compact={isMobile} text="Affected Packages" count={data.affectedPackages} />
                    ) : null}
                    {data.affectedConfiguration ? (
                        <Label compact={isMobile} text="Affected Configuration" count={data.affectedConfiguration} />
                    ) : null}
                </div>
            ) : null}
        </Link>
    );
};

export default ListItem;
